import { CustomerSatisfaction } from '@welbe/design-system';
import React, { createContext, useEffect, useState } from 'react';
import { getUserReviews, putUserReview } from 'services/services';
import { AuthenticationContext } from 'contexts/Auth';
import { useContextSelector } from 'use-context-selector';

type CsatProvoderType = {
	children: React.ReactNode;
};

type CsatContextType =
	| {
			fetched: boolean;
	  }
	| undefined;

type AdditionalInfoType = {
	reliablePlatform?: number;
	simplifiesDailyTasks?: number;
	intuitiveEasyPlatform?: number;
	understandEmployeeHealth?: number;
	platformImprovements?: string;
	recommendedFeatures?: string;
};

type ReviewStatus = 'pendent' | 'partial' | 'finished';

type UserReviewType = {
	id: string;
	score: number | null;
	type?: string;
	status: ReviewStatus;
	additionalInfo?: AdditionalInfoType | null;
};

const CsatContext = createContext<CsatContextType>(undefined);

const statusAllowedToShow = ['partial', 'pendent'];

const initialSteps = [
	{
		id: 'intuitiveEasyPlatform',
		title:
			'¿La plataforma es intuitiva y es sencillo aprender a trabajar con ella?',
	},
	{
		id: 'simplifiesDailyTasks',
		title:
			' ¿Las funciones integradas en la plataforma simplifican tu trabajo y tareas diarias?',
	},
	{
		id: 'reliablePlatform',
		title: '¿Es confiable trabajar con la plataforma y sus funcionalidades?',
	},
	{
		id: 'understandEmployeeHealth',
		title:
			'¿La plataforma te ayuda a comprender mejor la salud de los colaboradores dentro de la empresa?',
	},
	{
		id: 'easyToUseNoMedicalTechKnowledge',
		title:
			' ¿Es posible utilizar la plataforma sin necesidad de aprender sobre temas médicos o tecnológicos?',
	},
];

export const CsatProvider = ({ children }: CsatProvoderType): JSX.Element => {
	const { isAuthenticated } = useContextSelector(
		AuthenticationContext,
		(contextProps: any) => contextProps,
	);

	const [fetched, setFetched] = useState(false);
	const [isLoading, setLoading] = useState(false);
	const [visible, setVisible] = useState(false);
	const [userReviews, setUserReviews] = useState<UserReviewType[]>([]);
	const [experience, setExperience] = useState<any>({
		id: 'experience',
		title: '',
		score: 0,
	});
	const [evaluation, setEvaluation] = useState<any>({
		id: 'evaluation',
		steps: initialSteps,
	});
	const [feedback, setFeedback] = useState<any>({
		id: 'feedback',
		title: '',
	});

	const handleCancel = async (feedbacks?: any) => {
		const [review] = userReviews;

		try {
			setLoading(true);
			await putUserReview(experience.id, {
				id: experience.id,
				score: review.score,
				status: 'finished',
				additionalInfo: {
					...review.additionalInfo,
					platformImprovements: feedbacks?.improvement || undefined,
					recommendedFeatures: feedbacks?.features || undefined,
				},
			});
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const handleConfirmExperience = async (score: number): Promise<boolean> => {
		const [review] = userReviews;
		let succes = false;

		try {
			setLoading(true);
			const payload: any = {
				id: experience.id,
				score,
				status: 'partial',
				additionalInfo: {
					...review.additionalInfo,
				},
			};

			await putUserReview(experience.id, payload);
			setUserReviews([payload]);
			succes = true;
		} catch (error) {
			console.info(error);
		} finally {
			setLoading(false);
		}

		return succes;
	};

	const handleConfirmEvaluation = async (evaluationScore: any) => {
		const [key, value] = Object.entries(evaluationScore)[0];
		const [review] = userReviews;

		try {
			setLoading(true);
			const payload: any = {
				id: experience.id,
				score: review.score,
				status: 'partial',
				additionalInfo: {
					...review.additionalInfo,
					...evaluationScore,
				},
			};

			await putUserReview(experience.id, payload);
			setUserReviews([payload]);
			setEvaluation((prev: any) => ({
				...prev,
				steps: evaluation.steps.map((step: any) =>
					step.id === key ? { ...step, score: value } : step,
				),
			}));
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const handleConfirmFeedback = async (feedback: any) => {
		const [review] = userReviews;

		try {
			setLoading(true);
			const payload: any = {
				id: experience.id,
				score: review.score,
				status: 'finished',
				additionalInfo: {
					...review.additionalInfo,
					platformImprovements: feedback?.improvement,
					recommendedFeatures: feedback?.features,
				},
			};

			await putUserReview(experience.id, payload);
			setUserReviews([{ ...payload }]);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(true);
		}
	};

	const getPendingReview = (reviews: UserReviewType[]) => {
		return reviews.some(
			({ type, status }: UserReviewType) =>
				type === 'companyGeneralReview' && status !== 'finished',
		);
	};

	const functionBuilder = (data: any) => {
		const companyGeneralReview = data.find(
			({ type }: any) => type === 'companyGeneralReview',
		);

		const canShow = statusAllowedToShow.includes(companyGeneralReview.status);
		setExperience((prev: any) => ({
			...prev,
			id: companyGeneralReview.id,
			score: companyGeneralReview.score,
			title:
				'¿Como calificarías tu experiencia usando la plataforma al día de hoy?',
		}));

		setEvaluation((prev: any) => ({
			...prev,
			title:
				'¿Como calificarías tu experiencia usando la plataforma al día de hoy?',
			steps: evaluation.steps.map((step: any) => ({
				...step,
				score: companyGeneralReview?.additionalInfo?.[step.id] || 0,
			})),
		}));

		setFeedback((prev: any) => ({
			...prev,
			title:
				'¿Como calificarías tu experiencia usando la plataforma al día de hoy?',
		}));

		setFetched(true);
		setVisible(canShow);
		setUserReviews(data);
	};

	const fetchUserReviews = async () => {
		try {
			const { data } = await getUserReviews();
			const hasPendingReview = getPendingReview(data.content);

			if (hasPendingReview) {
				functionBuilder(data.content);
			}
		} catch (error) {
			console.error(error);
		}
	};

	useEffect(() => {
		if (!isAuthenticated) {
			setFetched(false);
		}

		if (isAuthenticated && !fetched) {
			fetchUserReviews();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAuthenticated]);

	return (
		<CsatContext.Provider value={{ fetched }}>
			<CustomerSatisfaction
				isLoading={isLoading}
				visible={visible}
				onClose={() => setVisible(false)}
				onCancelCustomerSatisfaction={handleCancel}
				options={{
					experience: {
						...experience,
						experienceConfirm: handleConfirmExperience,
					},
					evaluation: {
						...evaluation,
						evaluationConfirm: handleConfirmEvaluation,
						evaluationClose: () => null,
					},
					feedback: {
						...feedback,
						feedbackConfirm: handleConfirmFeedback,
						feedbackClose: () => null,
					},
				}}
			/>
			{children}
		</CsatContext.Provider>
	);
};
